import React, { useEffect, useState } from 'react';

import { useRouteMatch } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Box, Button, Checkbox, FormControlLabel, FormGroup, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import PropertyUploadModal from '~/components/Vendors/Properties/PropertyUploadModal';

import { formatDateShortDD, isAfter } from '~/services/TimeService';

interface Props {
  vendorId: string;
  properties: Array<App.Property>;
  offers: Array<App.Offer>;
}

interface PropertyRow {
  id: string;
  name: string;
  hotel_code?: string;
  isDeleted: boolean;
  linkedOffers: Array<App.Offer>;
}

function hasLiveOffer(linkedOffers: Array<App.Offer>) {
  return linkedOffers.some((offer) => offer.book_by_date && isAfter(offer.book_by_date));
}

function formatBookByDate(bookByDate?: string) {
  if (!bookByDate) {
    return '-';
  }

  return formatDateShortDD(bookByDate);
}

function applyPropertyFilters(propertyRows: Array<PropertyRow>, hideDeleted: boolean, hideIfNotLive: boolean) {
  return propertyRows.filter((propertyRow) => {
    if (hideDeleted && propertyRow.isDeleted) {
      return false;
    }

    if (hideIfNotLive && !hasLiveOffer(propertyRow.linkedOffers)) {
      return false;
    }

    return true;
  });
}

export default function PropertiesPage(props: Props) {
  const {
    params: { id_vendor: vendorId },
  } = useRouteMatch<{ id_vendor: string }>();

  const { offers } = props;
  const [propertyRows, setPropertyRows] = useState<Array<PropertyRow>>([]);
  const [filteredPropertyRows, setFilteredPropertyRows] = useState<Array<PropertyRow>>([]);
  const [hideDeleted, setHideDeleted] = useState(true);
  const [hideIfNotLive, setHideIfNotLive] = useState(false);

  useEffect(() => {
    const mappedPropertyRows: Array<PropertyRow> = props.properties.map(({ id, hotel_code, name, deleted_at }) => {
      const linkedOffers = offers.filter((offer) => offer.packages?.some((pkg) => pkg.fk_property_id === id));

      return {
        name: deleted_at ? `${name} (DELETED)` : name,
        id,
        hotel_code,
        isDeleted: !!deleted_at,
        linkedOffers,
      };
    });

    setPropertyRows(mappedPropertyRows);
  }, [props.properties, offers]);

  useEffect(() => {
    setFilteredPropertyRows(applyPropertyFilters(propertyRows, hideDeleted, hideIfNotLive));
  }, [propertyRows, hideDeleted, hideIfNotLive]);

  const editBtnFormatter = (id: string) => {
    return <Button href={`/vendors/${vendorId}/properties/${id}`}>Edit</Button>;
  };

  const columns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: 'Name',
      disableColumnMenu: true,
      minWidth: 150,
      flex: 1,
      display: 'flex',
    },
    {
      field: 'hotel_code',
      headerName: 'Hotel Code',
      disableColumnMenu: true,
      minWidth: 100,
      flex: 1,
      display: 'flex',
    },
    {
      field: 'offerName',
      flex: 2,
      headerName: 'Linked offers',
      disableColumnMenu: true,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PropertyRow>) => {
        return (
          <Box component="ul" sx={{ my: 1, pl: 2.5 }}>
            {(params.row.linkedOffers || []).map((offer) => (
              <li key={offer.id_salesforce_external}>{offer.opportunity_name || offer.name}</li>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'offerId',
      flex: 2,
      headerName: 'Linked offer IDs',
      disableColumnMenu: true,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PropertyRow>) => {
        return (
          <Box component="ul" sx={{ my: 1, pl: 2.5 }}>
            {(params.row.linkedOffers || []).map((offer) => (
              <li key={offer.id_salesforce_external}>{offer.id_salesforce_external}</li>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'offerBookBy',
      flex: 2,
      headerName: 'Offer Book By',
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PropertyRow>) => {
        return (
          <Box component="ul" sx={{ my: 1, pl: 2.5 }}>
            {(params.row.linkedOffers || []).map((offer) => (
              <li key={offer.id_salesforce_external}>{formatBookByDate(offer.book_by_date)}</li>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'edit',
      headerName: 'Edit',
      disableColumnMenu: true,
      sortable: false,
      minWidth: 70,
      flex: 0,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PropertyRow>) => {
        return editBtnFormatter(params.row?.id);
      },
    },
  ];

  return (
    <div>
      <FormGroup>
        <FormControlLabel
          control={<Checkbox checked={hideDeleted} onChange={(_, checked) => setHideDeleted(checked)} />}
          label="Hide deleted properties"
        />
        <FormControlLabel
          control={<Checkbox checked={hideIfNotLive} onChange={(_, checked) => setHideIfNotLive(checked)} />}
          label="Properties with active offers"
        />
      </FormGroup>

      <Box mt={2}>
        <DataGrid
          className="T-properties-page-table"
          rows={filteredPropertyRows || []}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={() => 'auto'}
          slots={{ pagination: null }}
          autoHeight
          disableRowSelectionOnClick
        />
      </Box>

      <Stack direction="row" spacing={2}>
        <Button component={Link} variant="contained" to={`/vendors/${vendorId}/properties`}>
          + New
        </Button>
        <PropertyUploadModal vendorId={vendorId} offers={offers} />
      </Stack>
    </div>
  );
}
